/**
 * Every string the interface shows, in both languages, in one file.
 *
 * Reference data is deliberately absent: the API returns `nameEn` and `nameZh` for every code it
 * knows (Group 17B), so a language switch never needs another round trip and this file never has
 * to be kept in step with the database.
 */
export const messages = {
  'app.name': { zh: 'ManaMatch', en: 'ManaMatch' },
  'app.tagline': { zh: '认真的人，认真地遇见', en: 'Serious people, met seriously' },

  'lang.switch': { zh: 'English', en: '中文' },
  'lang.label': { zh: '语言', en: 'Language' },

  'nav.profile': { zh: '我的资料', en: 'My profile' },
  'nav.preferences': { zh: '我的偏好', en: 'My preferences' },
  'nav.photo': { zh: '主照片', en: 'Photo' },
  'nav.account': { zh: '账号', en: 'Account' },
  'nav.signOut': { zh: '退出登录', en: 'Sign out' },

  'common.save': { zh: '保存', en: 'Save' },
  'common.saving': { zh: '保存中…', en: 'Saving…' },
  'common.saved': { zh: '已保存', en: 'Saved' },
  'common.cancel': { zh: '取消', en: 'Cancel' },
  'common.loading': { zh: '加载中…', en: 'Loading…' },
  'common.retry': { zh: '重试', en: 'Try again' },
  'common.next': { zh: '下一步', en: 'Next' },
  'common.back': { zh: '返回', en: 'Back' },
  'common.optional': { zh: '选填', en: 'Optional' },
  'common.required': { zh: '必填', en: 'Required' },
  'common.notSet': { zh: '未填写', en: 'Not set' },
  'common.yes': { zh: '是', en: 'Yes' },
  'common.no': { zh: '否', en: 'No' },
  'common.choose': { zh: '请选择', en: 'Choose…' },

  'auth.signIn': { zh: '登录', en: 'Sign in' },
  'auth.signUp': { zh: '注册', en: 'Create account' },
  'auth.email': { zh: '邮箱', en: 'Email' },
  'auth.password': { zh: '密码', en: 'Password' },
  'auth.passwordShow': { zh: '显示密码', en: 'Show password' },
  'auth.passwordHide': { zh: '隐藏密码', en: 'Hide password' },
  'auth.rememberMe': { zh: '记住我', en: 'Stay signed in' },
  'auth.forgot': { zh: '忘记密码？', en: 'Forgot your password?' },
  'auth.haveAccount': { zh: '已有账号？去登录', en: 'Already have an account? Sign in' },
  'auth.noAccount': { zh: '还没有账号？去注册', en: 'No account yet? Create one' },
  'auth.passwordHint': {
    zh: '至少 12 个字符。长句子比复杂符号更好记也更安全。',
    en: 'At least 12 characters. A long phrase beats a short tangle of symbols.',
  },

  'verify.title': { zh: '请验证你的邮箱', en: 'Confirm your email' },
  'verify.sentTo': { zh: '我们已把验证链接发到：', en: 'We sent a confirmation link to:' },
  'verify.whyRequired': {
    zh: '邮箱验证之后才能使用产品 —— 连接建立时，对方要联系得上你。',
    en: 'The product needs a confirmed address: when a connection forms, the other person has to be able to reach you.',
  },
  'verify.notArrived': { zh: '没收到？', en: "Didn't get it?" },
  'verify.resend': { zh: '重新发送', en: 'Resend it' },
  'verify.resent': { zh: '已重新发送，请查收（也看一下垃圾箱）。', en: 'Sent again — check your inbox, and your spam folder.' },
  'verify.checking': { zh: '正在验证…', en: 'Confirming…' },
  'verify.done': { zh: '邮箱已验证。', en: 'Your email is confirmed.' },
  'verify.goSignIn': { zh: '去登录', en: 'Sign in' },

  'forgot.title': { zh: '重置密码', en: 'Reset your password' },
  'forgot.explain': {
    zh: '填入你的邮箱，我们会发一条重置链接。',
    en: "Enter your email and we'll send a reset link.",
  },
  'forgot.sent': {
    zh: '如果这个邮箱注册过，重置链接已经发出。',
    en: 'If that address has an account, a reset link is on its way.',
  },
  'reset.title': { zh: '设置新密码', en: 'Set a new password' },
  'reset.newPassword': { zh: '新密码', en: 'New password' },
  'reset.done': { zh: '密码已更新。', en: 'Your password has been updated.' },

  'onboarding.title': { zh: '完成这几步就能开始匹配', en: 'A few steps before matching starts' },
  'onboarding.step.profile': { zh: '填写资料', en: 'Your profile' },
  'onboarding.step.photo': { zh: '上传主照片', en: 'Your photo' },
  'onboarding.step.preferences': { zh: '填写偏好', en: 'Your preferences' },
  'onboarding.complete': { zh: '已完成', en: 'Done' },
  'onboarding.incomplete': { zh: '待完成', en: 'To do' },

  'profile.title': { zh: '我的资料', en: 'My profile' },
  'profile.section.basics': { zh: '基本信息', en: 'Basics' },
  'profile.section.life': { zh: '生活状况', en: 'Life' },
  'profile.section.work': { zh: '教育与工作', en: 'Education and work' },
  'profile.section.about': { zh: '自我介绍', en: 'About you' },
  'profile.displayName': { zh: '昵称', en: 'Nickname' },
  'profile.familyName': { zh: '姓', en: 'Family name' },
  'profile.givenName': { zh: '名', en: 'Given name' },
  'profile.gender': { zh: '性别', en: 'Gender' },
  'profile.gender.Male': { zh: '男', en: 'Male' },
  'profile.gender.Female': { zh: '女', en: 'Female' },
  'profile.dateOfBirth': { zh: '出生日期', en: 'Date of birth' },
  'profile.heightCm': { zh: '身高（厘米）', en: 'Height (cm)' },
  'profile.location': { zh: '所在地', en: 'Where you live' },
  'profile.nzStatus': { zh: '新西兰身份', en: 'NZ status' },
  'profile.maritalStatus': { zh: '婚姻状况', en: 'Marital status' },
  'profile.education': { zh: '学历', en: 'Education' },
  'profile.employment': { zh: '工作状态', en: 'Employment' },
  'profile.occupation': { zh: '职业类别', en: 'Occupation' },
  'profile.smoking': { zh: '吸烟', en: 'Smoking' },
  'profile.alcohol': { zh: '饮酒', en: 'Drinking' },
  'profile.religion': { zh: '宗教信仰', en: 'Religion' },
  'profile.relocation': { zh: '可以在新西兰境内搬迁', en: 'Willing to move within NZ' },
  'profile.selfIntroduction': { zh: '自我介绍', en: 'About you' },
  'profile.hobbies': { zh: '兴趣爱好', en: 'Interests' },
  'profile.privateNote': {
    zh: '姓名只在连接建立后对对方开放，任何付费都买不到。',
    en: 'Your name is released only after a connection forms. No payment can buy it.',
  },

  'photo.title': { zh: '主照片', en: 'Your photo' },
  'photo.required': {
    zh: '主照片是必填的 —— 没有照片的资料不会进入匹配。',
    en: 'A photo is required: a profile without one is never matched.',
  },
  'photo.notVisible': {
    zh: '付费之前谁也看不到它。对方要先解锁你的报告，才能看到照片。',
    en: 'Nobody sees it before they unlock your report. Not blurred, not partially — not at all.',
  },
  'photo.choose': { zh: '选择照片', en: 'Choose a photo' },
  'photo.replace': { zh: '更换照片', en: 'Replace photo' },
  'photo.remove': { zh: '删除照片', en: 'Remove photo' },
  'photo.uploading': { zh: '上传中…', en: 'Uploading…' },
  'photo.limits': {
    zh: 'JPEG / PNG / WebP，不超过 8 MB，短边至少 200 像素。',
    en: 'JPEG, PNG or WebP, up to 8 MB, at least 200 pixels on the shorter side.',
  },
  'photo.stripped': {
    zh: '上传后我们会重新编码，清掉照片里的拍摄地点等信息。',
    en: 'We re-encode what you upload, which removes the location and other data cameras embed.',
  },
  'photo.status.Approved': { zh: '已通过', en: 'Approved' },
  'photo.status.Pending': { zh: '审核中', en: 'Being checked' },
  'photo.status.Rejected': { zh: '未通过', en: 'Rejected' },
  'photo.status.Flagged': { zh: '已下线', en: 'Taken down' },
  'photo.none': { zh: '还没有照片', en: 'No photo yet' },

  'contact.title': { zh: '联系方式', en: 'How to reach you' },
  'contact.required': {
    zh: '至少填一项。连接建立之后，对方就是用它联系你的。',
    en: 'At least one. It is what the other person uses to reach you once a connection forms.',
  },
  'contact.redline': {
    zh: '在此之前谁也看不到 —— 任何付费都买不到联系方式，连接建立时双方同时开放。',
    en: 'Nobody sees it before then. No payment can buy contact details; they open for both people at the same moment.',
  },
  'contact.add': { zh: '添加一项', en: 'Add one' },
  'contact.remove': { zh: '删除', en: 'Remove' },
  'contact.type': { zh: '方式', en: 'Kind' },
  'contact.value': { zh: '内容', en: 'Detail' },
  'contact.type.Mobile': { zh: '手机', en: 'Mobile' },
  'contact.type.WeChat': { zh: '微信', en: 'WeChat' },
  'contact.type.WhatsApp': { zh: 'WhatsApp', en: 'WhatsApp' },
  'contact.type.Email': { zh: '邮箱', en: 'Email' },
  'contact.type.Line': { zh: 'Line', en: 'Line' },
  'contact.type.Other': { zh: '其它', en: 'Other' },
  'onboarding.step.contact': { zh: '填写联系方式', en: 'How to reach you' },
  'nav.contact': { zh: '联系方式', en: 'Contact' },

  'importance.NoPreference': { zh: '无所谓', en: 'No preference' },
  'importance.Prefer': { zh: '偏好', en: 'Prefer' },
  'importance.Important': { zh: '重要', en: 'Important' },
  'importance.Must': { zh: '必须', en: 'Must have' },

  'preferences.title': { zh: '我的偏好', en: 'My preferences' },
  'preferences.explain': {
    zh: '偏好只影响给你的推荐，不会展示给任何人。',
    en: 'Preferences shape what you are shown. Nobody else ever sees them.',
  },
  'preferences.importance': { zh: '重要程度', en: 'How much it matters' },
  'preferences.ageRange': { zh: '年龄范围', en: 'Age range' },
  'preferences.heightRange': { zh: '身高范围', en: 'Height range' },
  'preferences.from': { zh: '从', en: 'from' },
  'preferences.to': { zh: '到', en: 'to' },

  'completion.title': { zh: '完成度', en: 'Completeness' },
  'completion.missing': { zh: '还差这些：', en: 'Still needed:' },
  'completion.ready': { zh: '都齐了。', en: 'Everything is here.' },
  'completion.markComplete': { zh: '完成并进入匹配', en: 'Finish and start matching' },
  'completion.field.PRIMARY_PHOTO': { zh: '主照片', en: 'A photo' },

  'account.title': { zh: '账号', en: 'Account' },
  'account.email': { zh: '邮箱', en: 'Email' },
  'account.status': { zh: '状态', en: 'Status' },
  'account.emailConfirmed': { zh: '邮箱已验证', en: 'Email confirmed' },
  'account.emailUnconfirmed': { zh: '邮箱未验证', en: 'Email not confirmed' },

  'nav.matching': { zh: '匹配', en: 'Matching' },
  'nav.matches': { zh: '我的匹配', en: 'My matches' },
  'nav.invitations': { zh: '邀约', en: 'Invitations' },
  'nav.connection': { zh: '连接', en: 'Connection' },

  'matching.title': { zh: '匹配', en: 'Matching' },
  'matching.unlockable': { zh: '符合你要求的人数', en: 'People who fit what you asked for' },
  'matching.pendingInvites': { zh: '待你回应的邀约', en: 'Invitations waiting for you' },
  'matching.next': { zh: '下一位的契合度', en: 'How well the next one fits' },
  'matching.nextDimensions': { zh: '契合在这些方面：', en: 'They fit you on:' },
  'matching.noCandidates': {
    zh: '暂时没有符合条件的人。放宽一点偏好，或者过几天再来看。',
    en: 'Nobody fits right now. Loosen a preference, or come back in a few days.',
  },
  'matching.neverWho': {
    zh: '付费之前不会显示是谁 —— 你买的是队列里的下一位，不是某个指定的人。',
    en: 'Nobody is named before you pay. What you buy is the next person in the queue, not a chosen one.',
  },
  'matching.unlockNext': { zh: '解锁下一位', en: 'Unlock the next one' },
  'matching.price': { zh: 'NZ$20（未注册 GST，$20 即全款）', en: 'NZ$20 — no GST, that is the whole price' },
  'matching.blocked.ProfileIncomplete': {
    zh: '资料还没填完，先把它补齐。',
    en: 'Your profile is not finished yet.',
  },
  'matching.blocked.PreferenceIncomplete': {
    zh: '偏好还没填完，先把它补齐。',
    en: 'Your preferences are not finished yet.',
  },
  'matching.blocked.InActiveConnection': {
    zh: '你正处在一段连接里。结束之后才会继续匹配。',
    en: 'You are in a connection. Matching resumes when it ends.',
  },
  'matching.blocked.AccountNotActive': {
    zh: '账号当前不可用。',
    en: 'This account is not active.',
  },

  'band.Poor': { zh: '不契合', en: 'Poor' },
  'band.Fair': { zh: '基本契合', en: 'Fair' },
  'band.Good': { zh: '较契合', en: 'Good' },
  'band.Strong': { zh: '很契合', en: 'Strong' },
  'band.Excellent': { zh: '非常契合', en: 'Excellent' },

  'payment.redirecting': { zh: '正在前往支付页…', en: 'Taking you to the payment page…' },
  'payment.success.title': { zh: '支付完成', en: 'Payment received' },
  'payment.success.body': {
    zh: '解锁正在确认中。确认后，这位会出现在「我的匹配」里。',
    en: 'Your unlock is being confirmed. Once it is, this person appears under My matches.',
  },
  'payment.cancelled.title': { zh: '支付已取消', en: 'Payment cancelled' },
  'payment.cancelled.body': {
    zh: '没有扣款。你可以随时再试一次。',
    en: 'Nothing was charged. You can try again whenever you like.',
  },
  'payment.backToMatching': { zh: '回到匹配', en: 'Back to matching' },
  'payment.viewMatches': { zh: '查看我的匹配', en: 'See my matches' },

  'matches.title': { zh: '我的匹配', en: 'My matches' },
  'matches.empty': { zh: '还没有解锁过任何人。', en: 'You have not unlocked anyone yet.' },
  'matches.kept': {
    zh: '付过费的记录永久保留，包括已经结束的。',
    en: 'Everything you paid for stays here, including what has ended.',
  },
  'matches.viewReport': { zh: '查看报告', en: 'Open the report' },
  'matches.reInvite': { zh: '重新邀约', en: 'Invite again' },
  'matches.unlockedAt': { zh: '解锁于', en: 'Unlocked' },

  'state.Unlocked': { zh: '已解锁，未邀约', en: 'Unlocked, not invited' },
  'state.InvitationSent': { zh: '已发出邀约', en: 'Invitation sent' },
  'state.InvitationReceived': { zh: '收到邀约', en: 'Invitation received' },
  'state.DeclinedByThem': { zh: '对方拒绝', en: 'They declined' },
  'state.DeclinedByMe': { zh: '你已拒绝', en: 'You declined' },
  'state.CancelledByMe': { zh: '你已撤回', en: 'You withdrew' },
  'state.Expired': { zh: '已过期', en: 'Expired' },
  'state.Connected': { zh: '已连接', en: 'Connected' },
  'state.Closed': { zh: '已结束', en: 'Ended' },

  'report.title': { zh: '匹配报告', en: 'Match report' },
  'report.myRequirement': { zh: '我的要求', en: 'What I asked for' },
  'report.theirSituation': { zh: '对方的情况', en: 'How they are' },
  'report.hidden': { zh: '不公开，但符合你的要求', en: 'Not shown — but it matches what you asked for' },
  'report.theyFitMe': { zh: '对方符合我的程度', en: 'How well they fit you' },
  'report.iFitThem': { zh: '我符合对方的程度', en: 'How well you fit them' },
  'report.overall': { zh: '总体契合', en: 'Overall' },
  'report.noName': {
    zh: '姓名与联系方式不在报告里 —— 任何付费都买不到，只有建立连接后双方才同时看到。',
    en: 'No name and no contact details here. No payment buys them; they open for both of you only when a connection forms.',
  },
  'report.invite': { zh: '发出邀约', en: 'Send an invitation' },
  'report.note': { zh: '附言', en: 'A note' },
  'report.notePlaceholder': {
    zh: '写点什么（最多 200 字）。不要写联系方式 —— 会被拒绝。',
    en: 'Say something (200 characters). Not contact details — those are refused.',
  },
  'report.inviteSent': { zh: '邀约已发出，对方有七天时间回应。', en: 'Sent. They have seven days to reply.' },

  'invitations.title': { zh: '邀约', en: 'Invitations' },
  'invitations.incoming': { zh: '收到的', en: 'Received' },
  'invitations.outgoing': { zh: '发出的', en: 'Sent' },
  'invitations.none': { zh: '暂时没有。', en: 'Nothing here yet.' },
  'invitations.expiresAt': { zh: '到期', en: 'Expires' },
  'invitations.sevenDays': {
    zh: '邀约只有七天。过期之后，双方都不能再邀约对方。',
    en: 'An invitation lasts seven days. After that, neither of you can invite the other again.',
  },
  'invitations.open': { zh: '查看', en: 'Open' },
  'invitations.accept': { zh: '接受', en: 'Accept' },
  'invitations.decline': { zh: '拒绝', en: 'Decline' },
  'invitations.cancel': { zh: '撤回', en: 'Withdraw' },
  'invitations.cancelNote': {
    zh: '撤回不留痕迹，将来还可以再邀约同一个人。',
    en: 'Withdrawing leaves no mark; you can invite this person again later.',
  },
  'invitations.declineNote': {
    zh: '拒绝之后，这个人不会再出现在你的匹配里。',
    en: 'Once you decline, this person will not come up again.',
  },

  'invitee.title': { zh: '有人向你发出了邀约', en: 'Someone invited you' },
  'invitee.free': {
    zh: '这一页是免费的。它只说明你们在哪些方面契合，不包含照片、自述、职业，也没有分数。',
    en: 'This page is free. It shows where you fit and nothing of the person — no photo, no introduction, no occupation, and no score.',
  },
  'invitee.senderFitsMe': { zh: '对方符合你要求的程度', en: 'How well they fit what you asked for' },
  'invitee.earlyUnlock': { zh: '先看完整报告（NZ$10）', en: 'See the full report first (NZ$10)' },
  'invitee.acceptFree': { zh: '直接接受（免费）', en: 'Accept — free' },
  'invitee.acceptMeaning': {
    zh: '接受即建立连接，双方同时看到姓名与联系方式。',
    en: 'Accepting forms the connection: you both see the other name and contact details at the same moment.',
  },

  'connection.title': { zh: '连接', en: 'Connection' },
  'connection.none': { zh: '你现在没有进行中的连接。', en: 'You are not in a connection right now.' },
  'connection.since': { zh: '建立于', en: 'Connected' },
  'connection.contacts': { zh: '联系方式', en: 'How to reach them' },
  'connection.name': { zh: '姓名', en: 'Name' },
  'connection.bothSides': {
    zh: '这些信息双方是同时开放的 —— 对方此刻也看到了你的。',
    en: 'This opened for both of you at the same moment — they can see yours too.',
  },
  'connection.close': { zh: '结束这段连接', en: 'End this connection' },
  'connection.closeWarning': {
    zh: '任何一方都可以单独结束。结束之后双方立即可以重新匹配，而你们两人不会再被推荐给对方。',
    en: 'Either of you can end it alone. Both of you can match again straight away, and the two of you will never be recommended to each other again.',
  },
  'connection.closeReason': { zh: '原因（选填）', en: 'Reason (optional)' },
  'connection.confirm': { zh: '确认结束', en: 'Yes, end it' },

  'nav.admin': { zh: '后台', en: 'Admin' },

  'twofactor.title': { zh: '两步验证', en: 'Two-step verification' },
  'twofactor.required': {
    zh: '管理员必须开启两步验证 —— 没开之前，后台除了这一页什么都打不开。',
    en: 'Administrators must use two-step verification. Until it is on, this page is the only part of the console that opens.',
  },
  'twofactor.enabled': { zh: '已开启', en: 'On' },
  'twofactor.disabled': { zh: '未开启', en: 'Not set up' },
  'twofactor.verifiedThisSession': { zh: '本次登录已验证', en: 'Verified this session' },
  'twofactor.notVerifiedThisSession': {
    zh: '本次登录尚未验证 —— 退出后重新登录并输入验证码。',
    en: 'This session has not answered it. Sign out and sign in again to enter a code.',
  },
  'twofactor.recoveryLeft': { zh: '剩余恢复码', en: 'Recovery codes left' },
  'twofactor.start': { zh: '开始设置', en: 'Set it up' },
  'twofactor.scan': {
    zh: '用验证器 App 扫描下面的二维码，或手动输入这串密钥：',
    en: 'Scan this with an authenticator app, or type the key in by hand:',
  },
  'twofactor.appHint': {
    zh: 'Google Authenticator、Microsoft Authenticator、1Password 等都可以。',
    en: 'Google Authenticator, Microsoft Authenticator, 1Password — any of them will do.',
  },
  'twofactor.code': { zh: '验证器显示的六位数字', en: 'The six digits your app shows' },
  'twofactor.enable': { zh: '开启', en: 'Turn it on' },
  'twofactor.enabledJustNow': {
    zh: '已开启。下面这些恢复码只显示这一次 —— 手机丢了就靠它们，请立刻存到安全的地方。',
    en: 'It is on. These recovery codes are shown once and once only — they are the way back in if the phone is gone. Save them somewhere safe now.',
  },
  'twofactor.signInAgain': {
    zh: '请退出后重新登录，用验证码完成一次验证，后台才会打开。',
    en: 'Sign out and sign in again, answering with a code. The console opens after that.',
  },
  'twofactor.prompt': { zh: '请输入验证器上的六位数字', en: 'Enter the six digits from your authenticator' },
  'twofactor.verify': { zh: '验证', en: 'Verify' },

  'admin.title': { zh: '后台', en: 'Admin' },
  'admin.nav.reports': { zh: '照片举报', en: 'Photo reports' },
  'admin.nav.prices': { zh: '价格', en: 'Prices' },
  'admin.nav.settings': { zh: '运营参数', en: 'Settings' },
  'admin.nav.twofactor': { zh: '两步验证', en: 'Two-step verification' },
  'admin.audited': {
    zh: '这里的每一处修改都会写进审计日志：改了什么、改成什么、谁改的、什么时候。',
    en: 'Every change here is written to the audit log: what it was, what it became, who did it, and when.',
  },

  'admin.prices.title': { zh: '价格', en: 'Prices' },
  'admin.prices.current': { zh: '当前价格', en: 'Current price' },
  'admin.prices.new': { zh: '改为', en: 'Change to' },
  'admin.prices.inFlight': {
    zh: '改价只影响之后新建的订单。已经创建的订单保留它自己的金额 —— 买家付的是他当时看到的价。',
    en: 'A new price only affects orders created after it. An order already in flight keeps its own amount: a buyer pays what they were shown.',
  },

  'admin.settings.title': { zh: '运营参数', en: 'Settings' },
  'admin.settings.value': { zh: '值', en: 'Value' },
  'admin.settings.onlySeeded': {
    zh: '只能改已有的键，不能新增 —— 打错一个键名会造出一条没人读的设置，看起来像改了但没生效。',
    en: 'Only existing keys can be edited. A mistyped key would create a setting nothing reads, which looks exactly like a change that did not work.',
  },

  'admin.reports.title': { zh: '照片举报', en: 'Photo reports' },
  'admin.reports.none': { zh: '没有待处置的举报。', en: 'Nothing to look at.' },
  'admin.reports.count': { zh: '举报人数', en: 'Distinct reporters' },
  'admin.reports.status': { zh: '当前状态', en: 'Status' },
  'admin.reports.first': { zh: '首次', en: 'First' },
  'admin.reports.last': { zh: '最近', en: 'Latest' },
  'admin.reports.takedown': { zh: '下线', en: 'Take it down' },
  'admin.reports.restore': { zh: '恢复上线', en: 'Put it back' },
  'admin.reports.notify': { zh: '通知用户更换', en: 'Ask them to replace it' },
  'admin.reports.rule': {
    zh: '一次举报只通知，三个不同的人才会自动下线 —— 否则任何陌生人都能把一个用户静默踢出匹配池。',
    en: 'One report only notifies; it takes three different people to take a photo down. Otherwise any stranger could quietly remove a user from the matching pool.',
  },
  'admin.reports.takedownMeaning': {
    zh: '下线会让这位用户的资料变得不完整，从而退出匹配，并给他发一封请求更换照片的通知。',
    en: 'Taking it down makes that profile incomplete, which removes it from matching, and sends them a note asking for a different photo.',
  },

  'error.network': {
    zh: '连不上服务器。请检查网络后重试。',
    en: 'Could not reach the server. Check your connection and try again.',
  },
  'error.unexpected': {
    zh: '出了点问题。请重试；如果一直这样，请把这个编号发给我们：',
    en: 'Something went wrong. Try again — if it keeps happening, send us this reference:',
  },
  'error.signedOut': { zh: '登录已过期，请重新登录。', en: 'Your session expired. Please sign in again.' },
} as const

export type MessageKey = keyof typeof messages
